import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import {
  Button,
  ComboField,
  ExplanatoryCard,
  Modal,
  Pill,
  RadioGroup,
  TextInputBase,
  ToolTip,
  useSnackBar,
} from "@truevault/aquilo";
import type { ComboFieldItem } from "@truevault/aquilo";
import { LightBulbIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Form } from "react-final-form";
import {
  CollectionContext,
  DataAccessibility,
  DataDeletability,
  OrganizationDataRecipientDto,
  OrganizationDataSourceDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import type { VendorCategoryGroupMode } from "../../../util/vendorCategorization";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { RequestHandlingInstructionDto } from "../../../../common/service/server/dto/RequestHandlingInstructionDto";
import { PrivacyCenterIntegrationsDto } from "../../../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { OrganizationPublicId, ProcessingMethod } from "../../../../common/service/server/Types";
import { useActionRequest } from "../../../../common/hooks/api";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { SurveyAnswers, useSurvey } from "../../../../common/hooks/api/useSurvey";
import { Api } from "../../../../common/service/Api";
import {
  useCollectionGroups,
  useConsumerCollectionGroup,
  useEmploymentCollectionGroups,
} from "../../../hooks/useCollectionGroups";
import { useOrgDataSources, useRequestInstructions } from "../../../hooks/useOrganizationVendors";
import {
  hasDataRecipientDisclosedPICSelection,
  isDataRecipientDisclosureEnabled,
} from "../data-flows/DataFlowsDisclosuresPage";
import {
  isVendorDataSourcingComplete,
  RemoveSourceConfirmationModal,
  VendorSourcingCard,
} from "../data-flows/DataFlowsSourcesPage";
import { useReceivedForCollectionGroups } from "../../../hooks/useReceivedForCollectionGroups";
import { useIntegrationsSettings } from "../../../components/superorg/settings/IntegrationsSettings";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import {
  resolveDataAccessibilityForVendor,
  isShopify,
  isThirdPartyRecipient,
} from "../../../util/vendors";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../get-compliant/data-recipients/shared";
import { Routes } from "../../../root/routes";
import type { VendorInventoryItem } from "./vendorInventoryUtils";
import { VendorDataSellingSection } from "./VendorDataSellingSection";
import {
  VendorRecipientDisclosures,
  VendorRoleDefinition,
  VendorRoleRemoveLink,
} from "./VendorRecipientDisclosures";
import { VendorTargetedAdvertisingSection } from "./VendorTargetedAdvertisingSection";
import { ConnectedSystemVendorsSection } from "./ConnectedSystemVendorsSection";
import { MANAGER_VENDOR_CATEGORIES } from "./ManagedByVendorSection";
import { ManagesAdNetworksSection } from "./ManagesAdNetworksSection";
import { isConnectedSystemVendor } from "../../../util/vendorNesting";
import {
  doesVendorHaveSharingComplete,
  doesVendorHaveSellingComplete,
  hasConsumerCollectionContext,
  shouldShowDataSellingSection,
  shouldShowSharing,
} from "./vendorInventoryUtils";
import { DIRECT_MAIL_VENDORS_SLUG } from "../../../surveys/sharingSurvey";
import { parseMultiselectAnswerWithNoneOption } from "../../../surveys/survey";
import { isDirectMailEligibleVendor } from "../../../util/optOutWorkflowVendors";
import { isEmptyDataMap } from "../../../../common/service/resources/data-map/dataMapHelpers";
import {
  BadgedTextPill,
  doesGdprInternationalDataTransferApplyForVendor,
  doesGppSignalApplyForVendor,
  doesVendorHaveUsClassification,
  getVendorDocumentationDisclosureState,
  isCaContractFieldRequiredForVendor,
  isGdprInternationalDataTransferComplete,
  isUsClassificationRequiredForVendor,
  isVendorCaContractDocumentationComplete,
  isVendorDocumentationClassified,
  isVendorGdprClassificationComplete,
  isVendorGppSignalDocumentationComplete,
  TruevaultVerifiedBadge,
  VendorDocumentationBrowserOptOutQuestion,
  VendorDocumentationCaContractQuestion,
  VendorDocumentationContactEmailField,
  VendorDocumentationEmailVendorSection,
  VendorDocumentationGdprClassificationQuestion,
  VendorDocumentationInternationalDataTransfersQuestion,
  VendorDocumentationPlatformRequestsQuestion,
  VendorDocumentationRegionsField,
  VendorDocumentationUsClassificationQuestion,
} from "../vendor-documentation/VendorDocumentationPage";
import { CONSUMER_SOURCES_SURVEY_NAME } from "../../get-compliant/data-map/ConsumerDataSourcesPage";
import { CONSUMER_DIRECTLY_SLUG } from "../../../surveys/dataMapSourcesSurvey";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../../stay-compliant/PrivacyNoticeChecklistItems";
import { DevInstructionsFinishedHrDataMapSlug } from "../../stay-compliant/hr-center/HRCenterDashboardPage";
import {
  gdprClassificationHelpText,
  gppSignalHelpText,
  usClassificationHelpText,
} from "../vendor-documentation/vendorDocumentationUtils";
import { hasShopifySNIAnswer, ShopifySNIQuestion } from "./ShopifySNIQuestion";
import {
  VENDOR_CATEGORY,
  VENDOR_CATEGORY_AD_NETWORK,
  VENDOR_CATEGORY_DATA_BROKER,
  VENDOR_KEY_GOOGLE_ANALYTICS,
  VENDOR_KEY_SHOPIFY,
  VENDOR_KEY_SHOPIFY_AUDIENCES,
  VENDOR_KEY_TRUEVAULT,
} from "../../../types/Vendor";

const VENDOR_KEYS_WITH_LOCKED_CATEGORY = new Set([
  VENDOR_KEY_GOOGLE_ANALYTICS,
  VENDOR_KEY_SHOPIFY,
  VENDOR_KEY_SHOPIFY_AUDIENCES,
  VENDOR_KEY_TRUEVAULT,
]);

// Prevents non-custom vendor category overrides to these canonical categories.
const NON_CUSTOM_VENDOR_CATEGORY_AUTOCOMPLETE_EXCLUDED_CATEGORIES = new Set([
  VENDOR_CATEGORY_AD_NETWORK,
  VENDOR_CATEGORY_DATA_BROKER,
]);

type SettingsTab =
  | "general"
  | "targeted-advertising"
  | "data-selling"
  | "data-disclosures"
  | "data-sourcing"
  | "vendor-documentation"
  | "api-integration"
  | "data-storage-deletion"
  | "dsar-workflows";

interface NavItem {
  id: SettingsTab;
  label: string;
  hasIndicator?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: "general", label: "General Settings" },
  { id: "targeted-advertising", label: "Targeted Advertising" },
  { id: "data-selling", label: "Data Selling" },
  { id: "data-disclosures", label: "Data Disclosures" },
  { id: "data-sourcing", label: "Data Sourcing" },
  { id: "vendor-documentation", label: "Vendor Documentation", hasIndicator: true },
  // TODO(saurabh): re-enable these after figuring out react-query cache busting issue
  // { id: "api-integration", label: "API Integration", hasIndicator: true },
  // { id: "data-storage-deletion", label: "Data Storage and Deletion", hasIndicator: true },
  // { id: "dsar-workflows", label: "DSAR Workflows", hasIndicator: true },
];

interface VendorDetailsModalProps {
  /** Inventory row (includes source/recipient flags and `recipientData` for settings tabs). */
  inventoryVendor: VendorInventoryItem;
  org: OrganizationDto;
  /** Full vendor list (e.g. targeted advertising “managed by another vendor”). */
  allVendors: VendorInventoryItem[];
  /** True when any collection group has collected PICs (from parent; avoids loading the data map for tab visibility). */
  hasAnyCollectedPics: boolean;
  existingCategories: Set<string>;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  removeDataSource: (vendorId: string) => Promise<void>;
  removeRecipientOnly: (vendorId: string) => Promise<void>;
  isDisabledWhileLoading: boolean;
  setIsDisabledWhileLoading: (value: boolean) => void;
  setGoogleAnalyticsDataSharingAnswer: (newAnswer: boolean) => Promise<void>;
  setShopifySNIAnswer: (newAnswer: boolean) => Promise<void>;
  onClose: () => void;
  onNext: () => void;
  /** Parent opens the same remove confirmation as the sidebar “Remove Vendor” control (e.g. `RemoveVendorConfirmationModal` on the inventory page). */
  onDelete: () => void;
  deleteDisabled: boolean;
  /** "Remove as recipient/source" links are hidden for HR inventory. */
  categoryGroupMode: VendorCategoryGroupMode;
  pageContext: CollectionContext;
}

export function VendorDetailsModal({
  inventoryVendor,
  org,
  allVendors,
  hasAnyCollectedPics,
  existingCategories,
  updateVendor,
  removeDataSource,
  removeRecipientOnly,
  isDisabledWhileLoading,
  setIsDisabledWhileLoading,
  setGoogleAnalyticsDataSharingAnswer,
  setShopifySNIAnswer,
  onClose,
  onNext,
  onDelete,
  deleteDisabled,
  pageContext,
}: VendorDetailsModalProps) {
  const recipient = inventoryVendor.recipientData!;
  const contentContainerRef = useRef<HTMLDivElement>(null);
  /** Unique HTML `name` prefix for all RadioGroups so fields do not collide with the inventory slideout. */
  const modalRadioNamePrefix = `vendor-details-modal-${inventoryVendor.vendorId}`;
  const { answers: sellingAndSharingAnswers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);
  const isHrContext = pageContext === "EMPLOYEE";
  const { answers: vendorDocumentationVisibilityAnswers } = useSurvey(
    org.id,
    isHrContext ? DEVELOPER_INSTRUCTIONS_SURVEY_NAME : CONSUMER_SOURCES_SURVEY_NAME,
  );
  const hasMetVendorDocumentationVisibilityRequirement = isHrContext
    ? vendorDocumentationVisibilityAnswers[DevInstructionsFinishedHrDataMapSlug] === "true"
    : vendorDocumentationVisibilityAnswers[CONSUMER_DIRECTLY_SLUG] !== undefined;
  const documentationVendors = useMemo(
    () => allVendors.flatMap((vendor) => (vendor.recipientData ? [vendor.recipientData] : [])),
    [allVendors],
  );
  const [category, setCategory] = useState<ComboFieldItem | null>(
    inventoryVendor.category
      ? { id: inventoryVendor.category, name: inventoryVendor.category }
      : null,
  );
  const [vendorName, setVendorName] = useState(inventoryVendor.name);
  const trimmedVendorName = vendorName.trim();
  const isInternalStorageLocation = inventoryVendor.isInternalDataStorageLocation === true;
  const isCategoryLocked =
    (!inventoryVendor.isCustom &&
      (inventoryVendor.defaultCategory === VENDOR_CATEGORY_AD_NETWORK ||
        inventoryVendor.defaultCategory === VENDOR_CATEGORY_DATA_BROKER)) ||
    (inventoryVendor.vendorKey != null &&
      VENDOR_KEYS_WITH_LOCKED_CATEGORY.has(inventoryVendor.vendorKey));

  const handleCategoryChange = async (newCategory: ComboFieldItem) => {
    if (isCategoryLocked || newCategory.name === category?.name) {
      return;
    }

    setCategory(newCategory);
    setIsDisabledWhileLoading(true);
    try {
      await updateVendor({
        vendorId: inventoryVendor.vendorId,
        vendorSettings: {
          ...recipient,
          category: newCategory.name,
        },
      });
    } finally {
      setIsDisabledWhileLoading(false);
    }
  };

  const showTargetedAdvertisingTab = shouldShowSharing(recipient, pageContext);
  const showDataSellingTab = shouldShowDataSellingSection(recipient, pageContext);
  const showDataSourcingTab = inventoryVendor.isSource;
  const showDataCollectionTab = hasAnyCollectedPics;
  const showVendorDocumentationTab = hasMetVendorDocumentationVisibilityRequirement;
  const vendorDocumentationComplete = isVendorDocumentationClassified(org, recipient, pageContext);
  const { data: dataMap } = useDataMap(org.id);
  const [consumerCollectionGroups, consumerCollectionGroupsRequest] = useCollectionGroups(org.id, [
    "BUSINESS_TO_CONSUMER",
  ]);
  const [consumerCollectionGroup] = useConsumerCollectionGroup(org.id);
  // HR's Vendor List opens this modal too: its disclosures and sourcing are per employment group.
  const [employmentGroups, employmentGroupsRequest] = useEmploymentCollectionGroups(org.id);
  const dataCollectionGroups = isHrContext ? employmentGroups : consumerCollectionGroups;
  const dataCollectionGroupsRequest = isHrContext
    ? employmentGroupsRequest
    : consumerCollectionGroupsRequest;
  const isDataCollectionComplete = useMemo(() => {
    if (!dataMap || !dataCollectionGroupsRequest.success) {
      return false;
    }

    return dataCollectionGroups.every((group) => {
      if (!isDataRecipientDisclosureEnabled(dataMap, recipient, group)) {
        return true;
      }

      return hasDataRecipientDisclosedPICSelection(dataMap, recipient, [group]);
    });
  }, [dataCollectionGroups, dataCollectionGroupsRequest.success, dataMap, recipient]);
  const isDataSourcingComplete = useMemo(() => {
    if (!showDataSourcingTab) {
      return true;
    }
    const sourcingGroupIds = isHrContext
      ? employmentGroups.map((group) => group.id)
      : consumerCollectionGroup
      ? [consumerCollectionGroup.id]
      : [];
    if (sourcingGroupIds.length === 0) {
      return false;
    }

    return isVendorDataSourcingComplete(dataMap, sourcingGroupIds, inventoryVendor.vendorId);
  }, [
    showDataSourcingTab,
    isHrContext,
    employmentGroups,
    consumerCollectionGroup,
    dataMap,
    inventoryVendor.vendorId,
  ]);
  const tabCompletionById: Partial<Record<SettingsTab, boolean>> = useMemo(
    () => ({
      general: !isShopify(recipient) || hasShopifySNIAnswer(sellingAndSharingAnswers),
      "targeted-advertising": doesVendorHaveSharingComplete(recipient),
      "data-selling": doesVendorHaveSellingComplete(recipient, sellingAndSharingAnswers),
      "data-disclosures": isDataCollectionComplete,
      "data-sourcing": isDataSourcingComplete,
      "vendor-documentation": vendorDocumentationComplete,
    }),
    [
      recipient,
      sellingAndSharingAnswers,
      isDataCollectionComplete,
      isDataSourcingComplete,
      vendorDocumentationComplete,
    ],
  );

  const navItems = useMemo(() => {
    if (isInternalStorageLocation) {
      return NAV_ITEMS.filter((item) => item.id === "general").map((item) => ({
        ...item,
        hasIndicator: false,
      }));
    }

    return NAV_ITEMS.filter(
      (item) =>
        (item.id !== "targeted-advertising" || showTargetedAdvertisingTab) &&
        (item.id !== "data-selling" || showDataSellingTab) &&
        (item.id !== "data-disclosures" || showDataCollectionTab) &&
        (item.id !== "data-sourcing" || showDataSourcingTab) &&
        (item.id !== "vendor-documentation" || showVendorDocumentationTab),
    ).map((item) => {
      if (item.id === "general") {
        return { ...item, hasIndicator: !tabCompletionById[item.id] };
      }
      if (item.id === "targeted-advertising") {
        return { ...item, hasIndicator: !tabCompletionById[item.id] };
      }
      if (item.id === "data-selling") {
        return { ...item, hasIndicator: !tabCompletionById[item.id] };
      }
      if (item.id === "data-disclosures") {
        return { ...item, hasIndicator: !tabCompletionById[item.id] };
      }
      if (item.id === "data-sourcing") {
        return { ...item, hasIndicator: !tabCompletionById[item.id] };
      }
      if (item.id === "vendor-documentation") {
        return { ...item, hasIndicator: !vendorDocumentationComplete };
      }
      return item;
    });
  }, [
    showTargetedAdvertisingTab,
    showDataSellingTab,
    showDataCollectionTab,
    showDataSourcingTab,
    showVendorDocumentationTab,
    tabCompletionById,
    vendorDocumentationComplete,
    isInternalStorageLocation,
  ]);

  const [activeTab, setActiveTabInternal] = useState<SettingsTab>("general");
  const setActiveTab = (tab: SettingsTab) => {
    if (tab === "targeted-advertising" && !showTargetedAdvertisingTab) {
      return;
    }
    if (tab === "data-selling" && !showDataSellingTab) {
      return;
    }
    if (tab === "data-disclosures" && !showDataCollectionTab) {
      return;
    }
    if (tab === "data-sourcing" && !showDataSourcingTab) {
      return;
    }
    if (tab === "vendor-documentation" && !showVendorDocumentationTab) {
      return;
    }
    setActiveTabInternal(tab);
  };

  useEffect(() => {
    if (!showTargetedAdvertisingTab && activeTab === "targeted-advertising") {
      setActiveTabInternal("general");
    }
  }, [showTargetedAdvertisingTab, activeTab]);

  useEffect(() => {
    if (!showDataSellingTab && activeTab === "data-selling") {
      setActiveTabInternal("general");
    }
  }, [showDataSellingTab, activeTab]);

  useEffect(() => {
    if (!showDataCollectionTab && activeTab === "data-disclosures") {
      setActiveTabInternal("general");
    }
  }, [showDataCollectionTab, activeTab]);

  useEffect(() => {
    if (!showDataSourcingTab && activeTab === "data-sourcing") {
      setActiveTabInternal("general");
    }
  }, [showDataSourcingTab, activeTab]);

  useEffect(() => {
    if (!showVendorDocumentationTab && activeTab === "vendor-documentation") {
      setActiveTabInternal("general");
    }
  }, [showVendorDocumentationTab, activeTab]);

  const currentNavIndex = navItems.findIndex((item) => item.id === activeTab);
  const isLastNavTab =
    navItems.length > 0 && currentNavIndex >= 0 && currentNavIndex === navItems.length - 1;
  // Vendor documentation often can't be finished yet (it may need the vendor's paperwork), so it
  // keeps its incomplete dot but never blocks Next/Done.
  const isActiveTabComplete =
    activeTab === "vendor-documentation" || (tabCompletionById[activeTab] ?? true);

  const handleFooterNext = async () => {
    if (isDisabledWhileLoading) return;

    if (isInternalStorageLocation) {
      if (!trimmedVendorName || trimmedVendorName === inventoryVendor.name) return;

      setIsDisabledWhileLoading(true);
      try {
        await updateVendor({
          vendorId: inventoryVendor.vendorId,
          vendorSettings: {
            ...recipient,
            name: trimmedVendorName,
          },
        });
        onNext();
      } finally {
        setIsDisabledWhileLoading(false);
      }
      return;
    }

    if (!isActiveTabComplete) return;
    if (navItems.length === 0) {
      onNext();
      return;
    }
    if (currentNavIndex === -1) {
      setActiveTab(navItems[0].id);
      return;
    }
    if (currentNavIndex >= navItems.length - 1) {
      onNext();
    } else {
      setActiveTab(navItems[currentNavIndex + 1].id);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          {inventoryVendor.logoUrl ? (
            <img
              src={inventoryVendor.logoUrl}
              alt={inventoryVendor.name}
              className="h-6 w-6 flex-shrink-0"
            />
          ) : (
            <div className="h-6 w-6 rounded bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-primary-600">
                {inventoryVendor.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span>{inventoryVendor.name} Settings</span>
        </div>
      }
      alignText="left"
      alignButton="right"
      firstButton="Cancel"
      onFirstButtonClick={onClose}
      secondButton={isLastNavTab ? "Done" : "Next"}
      secondButtonColor="primary"
      secondButtonDisabled={
        isDisabledWhileLoading ||
        (isInternalStorageLocation
          ? !trimmedVendorName || trimmedVendorName === inventoryVendor.name
          : !isActiveTabComplete)
      }
      onSecondButtonClick={handleFooterNext}
      showOverlay
      closeButton
      closeOnOverlayClick
      onClose={onClose}
      maxPanelWidth="max-w-[944px]"
      contentContainerClasses="w-full"
      contentPadding="px-6 pt-4 pb-0"
      buttonPadding="px-6 py-4"
    >
      {/* Body — loading overlay matches vendor inventory slideout */}
      <div className="relative flex h-[443px] min-h-0 text-[14px]">
        {isDisabledWhileLoading && (
          <div className="absolute top-0 left-0 w-full h-full bg-white bg-opacity-50 z-10">
            <div className="flex items-center justify-center w-full h-full">
              <CircularProgress size="6em" />
            </div>
          </div>
        )}
        {/* Left Sidebar Navigation */}
        <nav className="w-[182px] shrink-0">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={clsx(
                "w-full flex items-center gap-[12px] rounded-[6px] py-[8px] text-[14px] text-left font-medium",
                activeTab === item.id
                  ? "bg-primary-100 text-primary-600"
                  : "text-neutral-700 hover:bg-gray-50",
                item.hasIndicator ? "pl-[8px]" : "pl-[32px]",
              )}
            >
              {item.hasIndicator && (
                <span className="h-2 w-2 rounded-full bg-amber-400 flex-shrink-0" />
              )}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Right Content Area */}
        <div
          ref={contentContainerRef}
          className="flex-1 pl-[64px] pr-[8px] pt-[12px] pb-[48px] overflow-y-auto"
          // Fade the last 40px so it's visible that the tab continues below the fold. The bottom
          // padding is taller than the fade, so fully scrolled content sits above it.
          style={{
            maskImage: "linear-gradient(to bottom, black calc(100% - 40px), transparent)",
            WebkitMaskImage: "linear-gradient(to bottom, black calc(100% - 40px), transparent)",
          }}
        >
          {activeTab === "general" && (
            <>
              <GeneralSettingsContent
                inventoryVendor={inventoryVendor}
                org={org}
                radioGroupNamePrefix={modalRadioNamePrefix}
                category={category}
                existingCategories={existingCategories}
                onCategoryChange={handleCategoryChange}
                isCategoryLocked={isCategoryLocked}
                vendorName={vendorName}
                onVendorNameChange={setVendorName}
                setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                onDelete={onDelete}
                deleteDisabled={deleteDisabled}
                extraSettings={
                  !isHrContext &&
                  !isConnectedSystemVendor(recipient) &&
                  MANAGER_VENDOR_CATEGORIES.has(inventoryVendor.category) && (
                    <ManagesAdNetworksSection
                      radioGroupNamePrefix={modalRadioNamePrefix}
                      org={org}
                      vendor={inventoryVendor}
                      allVendors={allVendors}
                      portalAutocompleteDropdown
                    />
                  )
                }
              />
              {isShopify(recipient) && (
                <ShopifySNIQuestion
                  radioGroupNamePrefix={modalRadioNamePrefix}
                  sellingAndSharingAnswers={sellingAndSharingAnswers}
                  setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                  setShopifySNIAnswer={setShopifySNIAnswer}
                  incompleteBadgePlacement="below-question"
                  className="mt-[24px]"
                />
              )}
              {isConnectedSystemVendor(recipient) && (
                <ConnectedSystemVendorsSection
                  org={org}
                  vendor={inventoryVendor}
                  allVendors={allVendors}
                  portalAutocompleteDropdown
                  onVendorAdded={() => {
                    const container = contentContainerRef.current;
                    container?.scrollTo({ top: container.scrollHeight, behavior: "auto" });
                  }}
                  className="mt-[24px]"
                />
              )}
            </>
          )}

          {activeTab === "targeted-advertising" && showTargetedAdvertisingTab && (
            <>
              <ExplanatoryCard
                variant="hairline"
                expandable={false}
                topBody={
                  <div>
                    <h3 className="text-[16px] leading-[24px] font-semibold text-gray-800 mb-[16px]">
                      What is Targeted Advertising?
                    </h3>
                    <p className="text-[14px] leading-[20px] text-neutral-500">
                      Targeted Advertising (also known as “Sharing”) involves using customer
                      personal information for purposes of behavioral, or interest-based advertising
                      across platforms or devices.
                    </p>
                  </div>
                }
              />
              <VendorTargetedAdvertisingSection
                radioGroupNamePrefix={modalRadioNamePrefix}
                vendor={inventoryVendor}
                org={org}
                allVendors={allVendors}
                portalAutocompleteDropdown
                onManagerAdded={() => {
                  const container = contentContainerRef.current;
                  container?.scrollTo({ top: container.scrollHeight, behavior: "auto" });
                }}
                updateVendor={updateVendor}
              />
            </>
          )}

          {activeTab === "data-selling" && showDataSellingTab && (
            <>
              <ExplanatoryCard
                variant="hairline"
                expandable={false}
                topBody={
                  <div>
                    <h3 className="text-[16px] leading-[24px] font-semibold text-gray-800 mb-[16px]">
                      What is Data Selling
                    </h3>
                    <p className="text-[14px] leading-[20px] text-neutral-500">
                      “Selling" includes exchanging personal data for money or anything else of
                      value (e.g., disclosing personal information and receiving a free service or
                      access to other data in exchange).
                    </p>
                  </div>
                }
              />
              <VendorDataSellingSection
                radioGroupNamePrefix={modalRadioNamePrefix}
                vendor={inventoryVendor}
                pageContext={pageContext}
                org={org}
                sellingAndSharingAnswers={sellingAndSharingAnswers}
                setGoogleAnalyticsDataSharingAnswer={setGoogleAnalyticsDataSharingAnswer}
                setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                updateVendor={updateVendor}
              />
            </>
          )}

          {activeTab === "data-disclosures" && showDataCollectionTab && (
            <VendorRecipientDisclosures
              vendor={recipient}
              org={org}
              collectionContext={pageContext}
              // Roles are removed from the tab that describes them, for this page's context only.
              headerAction={
                <RemoveAsRecipientLink
                  inventoryVendor={inventoryVendor}
                  removeRecipientOnly={removeRecipientOnly}
                  onRemoveVendor={onDelete}
                  removeVendorDisabled={deleteDisabled}
                  setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                />
              }
            />
          )}

          {activeTab === "data-sourcing" && showDataSourcingTab && (
            <DataSourcingContent
              inventoryVendor={inventoryVendor}
              org={org}
              collectionContext={pageContext}
              headerAction={
                <RemoveAsSourceLink
                  inventoryVendor={inventoryVendor}
                  removeDataSource={removeDataSource}
                  setIsDisabledWhileLoading={setIsDisabledWhileLoading}
                />
              }
            />
          )}

          {activeTab === "vendor-documentation" && showVendorDocumentationTab && (
            <VendorDocumentationContent
              vendor={recipient}
              org={org}
              updateVendor={updateVendor}
              isDisabledWhileLoading={isDisabledWhileLoading}
              setIsDisabledWhileLoading={setIsDisabledWhileLoading}
              sellingAndSharingAnswers={sellingAndSharingAnswers}
              allVendors={documentationVendors}
              collectionContext={pageContext}
            />
          )}

          {activeTab === "api-integration" && <ApiIntegrationContent />}

          {activeTab === "data-storage-deletion" && (
            <DataStorageDeletionContent
              radioGroupNamePrefix={modalRadioNamePrefix}
              vendor={recipient}
              org={org}
              updateVendor={updateVendor}
              isDisabledWhileLoading={isDisabledWhileLoading}
              setIsDisabledWhileLoading={setIsDisabledWhileLoading}
            />
          )}

          {activeTab === "dsar-workflows" && <PlaceholderContent title="DSAR Workflows" />}
        </div>
      </div>
    </Modal>
  );
}

interface GeneralSettingsContentProps {
  inventoryVendor: VendorInventoryItem;
  org: OrganizationDto;
  radioGroupNamePrefix: string;
  category: ComboFieldItem | null;
  existingCategories: Set<string>;
  onCategoryChange: (category: ComboFieldItem) => void;
  isCategoryLocked: boolean;
  vendorName: string;
  onVendorNameChange: (name: string) => void;
  setIsDisabledWhileLoading: (value: boolean) => void;
  onDelete: () => void;
  deleteDisabled: boolean;
  /** Category-specific questions, shown above Remove Vendor. */
  extraSettings?: React.ReactNode;
}

function GeneralSettingsContent({
  inventoryVendor,
  org,
  radioGroupNamePrefix,
  category,
  existingCategories,
  onCategoryChange,
  isCategoryLocked,
  vendorName,
  onVendorNameChange,
  setIsDisabledWhileLoading,
  onDelete,
  deleteDisabled,
  extraSettings,
}: GeneralSettingsContentProps) {
  const recipient = inventoryVendor.recipientData;

  const categoryItems: ComboFieldItem[] = useMemo(() => {
    const categories = Array.from(
      new Set([...Object.values(VENDOR_CATEGORY), ...existingCategories]),
    ).filter(
      (category) =>
        inventoryVendor.isCustom ||
        !NON_CUSTOM_VENDOR_CATEGORY_AUTOCOMPLETE_EXCLUDED_CATEGORIES.has(category),
    );
    categories.sort();
    return categories.map((cat) => ({ id: cat, name: cat }));
  }, [existingCategories, inventoryVendor.isCustom]);

  if (inventoryVendor.isInternalDataStorageLocation) {
    return (
      <div className="space-y-6">
        <TextInputBase
          field={`internal-storage-location-name-${inventoryVendor.vendorId}`}
          label="Vendor Name"
          value={vendorName}
          onChange={(event) => onVendorNameChange(event.target.value)}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Vendor Name (read-only display) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
        <div className="flex items-center gap-3 px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
          {inventoryVendor.logoUrl ? (
            <img
              src={inventoryVendor.logoUrl}
              alt={inventoryVendor.name}
              className="h-5 w-5 flex-shrink-0"
            />
          ) : (
            <div className="h-5 w-5 rounded bg-primary-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-primary-600">
                {inventoryVendor.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span className="text-sm text-gray-900">{inventoryVendor.name}</span>
        </div>
      </div>

      {/* Category ComboField */}
      <div>
        <ComboField
          items={categoryItems}
          value={category}
          onChange={onCategoryChange}
          label="Category"
          placeholder="Select or type a category..."
          showOptionsOnFocus
          disabled={isCategoryLocked}
        />
      </div>

      {recipient && isDirectMailEligibleVendor(recipient) && (
        <div className="mt-[6px]">
          <VendorDirectMailSetting
            recipient={recipient}
            orgId={org.id}
            radioGroupNamePrefix={radioGroupNamePrefix}
            setIsDisabledWhileLoading={setIsDisabledWhileLoading}
          />
        </div>
      )}

      {extraSettings}

      <div className="flex justify-end">
        <Button color="warning-secondary" onClick={onDelete} disabled={deleteDisabled}>
          Remove Vendor
        </Button>
      </div>
    </div>
  );
}

function VendorDirectMailSetting({
  recipient,
  orgId,
  radioGroupNamePrefix,
  setIsDisabledWhileLoading,
}: {
  recipient: OrganizationDataRecipientDto;
  orgId: OrganizationPublicId;
  radioGroupNamePrefix: string;
  setIsDisabledWhileLoading: (value: boolean) => void;
}) {
  const snackbar = useSnackBar();

  const { answers, setAnswer } = useSurvey(orgId, SELLING_AND_SHARING_SURVEY_NAME);

  const directMailVendorIds = parseMultiselectAnswerWithNoneOption(
    answers,
    DIRECT_MAIL_VENDORS_SLUG,
  );
  const isIncludedInDirectMail = directMailVendorIds.includes(recipient.id);

  const updateIncludedInDirectMail = async (isIncluded: boolean) => {
    const newDirectMailVendorIds = new Set(directMailVendorIds);
    if (isIncluded) {
      newDirectMailVendorIds.add(recipient.id);
    } else {
      newDirectMailVendorIds.delete(recipient.id);
    }

    setIsDisabledWhileLoading(true);
    try {
      await setAnswer(DIRECT_MAIL_VENDORS_SLUG, JSON.stringify(Array.from(newDirectMailVendorIds)));
    } catch (error) {
      snackbar("Failed to update direct mail setting", { variant: "error" });
    } finally {
      setIsDisabledWhileLoading(false);
    }
  };

  return (
    <div className="flex justify-between items-center">
      <div>
        <div className="font-medium text-gray-700">Vendor is a direct mail vendor</div>
        <div className="text-neutral-500 text-[14px] leading-[20px] mt-[4px]">
          Direct promotional mail to consumers is sent with this vendor
        </div>
      </div>
      <RadioGroup
        name={`${radioGroupNamePrefix}-includeInDirectMail`}
        direction="horizontal"
        options={[
          { label: "Yes", value: "YES" },
          { label: "No", value: "NO" },
        ]}
        selectedValue={isIncludedInDirectMail ? "YES" : "NO"}
        onChange={async (newValue: string) => {
          await updateIncludedInDirectMail(newValue === "YES");
        }}
      />
    </div>
  );
}

const CONSUMER_CONTEXT: CollectionContext[] = ["CONSUMER"];
const EMPLOYEE_CONTEXT: CollectionContext[] = ["EMPLOYEE"];

/**
 * Drops the recipient role. A vendor that is also a source keeps that role (it stays on the Sources
 * page); otherwise losing its only role is the same as "Remove Vendor", so it goes through that
 * flow and its confirmation.
 */
function RemoveAsRecipientLink({
  inventoryVendor,
  removeRecipientOnly,
  onRemoveVendor,
  removeVendorDisabled,
  setIsDisabledWhileLoading,
}: {
  inventoryVendor: VendorInventoryItem;
  removeRecipientOnly: (vendorId: string) => Promise<void>;
  onRemoveVendor: () => void;
  removeVendorDisabled: boolean;
  setIsDisabledWhileLoading: (value: boolean) => void;
}) {
  const { vendorId, name: vendorName, isSource } = inventoryVendor;
  const showSnackbar = useSnackBar();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!isSource && removeVendorDisabled) {
    return null;
  }

  return (
    <>
      <VendorRoleRemoveLink onClick={() => (isSource ? setConfirmOpen(true) : onRemoveVendor())}>
        Remove as recipient
      </VendorRoleRemoveLink>
      {confirmOpen && (
        <Modal
          title={`Remove ${vendorName} as a recipient?`}
          body={`${vendorName} will stay a data source on your Sources page. Its recipient settings and disclosures will be removed from your Vendor List.`}
          alignText="left"
          alignButton="right"
          firstButton="Cancel"
          onFirstButtonClick={() => setConfirmOpen(false)}
          secondButton="Remove as recipient"
          secondButtonColor="warning"
          onSecondButtonClick={async () => {
            setConfirmOpen(false);
            setIsDisabledWhileLoading(true);
            try {
              await removeRecipientOnly(vendorId);
            } catch {
              showSnackbar(`Couldn't remove ${vendorName} as a recipient. Please try again.`, {
                variant: "error",
              });
              return;
            } finally {
              setIsDisabledWhileLoading(false);
            }
            showSnackbar(`${vendorName} was removed from your Vendor List.`, {
              variant: "info",
              body: "It's still a data source on your Sources page.",
            });
          }}
          showOverlay
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
}

/** Drops the source role; the vendor stays in the Vendor List as a recipient. */
function RemoveAsSourceLink({
  inventoryVendor,
  removeDataSource,
  setIsDisabledWhileLoading,
}: {
  inventoryVendor: VendorInventoryItem;
  removeDataSource: (vendorId: string) => Promise<void>;
  setIsDisabledWhileLoading: (value: boolean) => void;
}) {
  const { vendorId, name: vendorName } = inventoryVendor;
  const showSnackbar = useSnackBar();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <VendorRoleRemoveLink onClick={() => setConfirmOpen(true)}>
        Remove as source
      </VendorRoleRemoveLink>
      {confirmOpen && (
        <RemoveSourceConfirmationModal
          sourceName={vendorName}
          onConfirm={async () => {
            setConfirmOpen(false);
            setIsDisabledWhileLoading(true);
            try {
              await removeDataSource(vendorId);
            } catch {
              showSnackbar(`Couldn't remove ${vendorName} as a source. Please try again.`, {
                variant: "error",
              });
              return;
            } finally {
              setIsDisabledWhileLoading(false);
            }
            showSnackbar(`${vendorName} was removed as a source.`, { variant: "info" });
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
}

function DataSourcingContent({
  inventoryVendor,
  org,
  collectionContext,
  headerAction,
}: {
  inventoryVendor: VendorInventoryItem;
  org: OrganizationDto;
  collectionContext: CollectionContext;
  headerAction?: React.ReactNode;
}) {
  const isHr = collectionContext === "EMPLOYEE";
  const [collectionGroups] = useCollectionGroups(org.id);
  const [employmentGroups] = useEmploymentCollectionGroups(org.id);
  const { data: dataMap } = useDataMap(org.id);
  const [orgDataSources] = useOrgDataSources(org.id);
  const consumerCollectionGroup = collectionGroups.find(
    (group) => group.autocreationSlug === "ONLINE_SHOPPERS",
  );
  // Consumer sourcing is the online shoppers group; HR's covers every employment group, as on the
  // HR Sources page.
  const sourcingGroups = useMemo(
    () => (isHr ? employmentGroups : consumerCollectionGroup ? [consumerCollectionGroup] : []),
    [isHr, employmentGroups, consumerCollectionGroup],
  );
  const receivedData = useReceivedForCollectionGroups(
    org.id,
    sourcingGroups,
    isHr ? EMPLOYEE_CONTEXT : CONSUMER_CONTEXT,
  );

  const source = useMemo<OrganizationDataSourceDto | undefined>(() => {
    return orgDataSources?.find(
      (dataSource) =>
        dataSource.vendorId === inventoryVendor.vendorId &&
        (isHr
          ? dataSource.collectionContext.includes("EMPLOYEE")
          : hasConsumerCollectionContext(dataSource.collectionContext)),
    );
  }, [inventoryVendor.vendorId, orgDataSources, isHr]);

  if (
    sourcingGroups.length === 0 ||
    !dataMap ||
    isEmptyDataMap(dataMap) ||
    !orgDataSources ||
    !source
  ) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[12px]">
      <div>
        <div className="flex items-start justify-between gap-[12px]">
          <div className="flex flex-wrap items-center gap-x-[8px] text-[16px] leading-[24px] font-semibold text-neutral-700">
            <span>{source.name} is a</span>
            <Pill variant="completed" noMargin>
              Source
            </Pill>
            <span>of the following data:</span>
          </div>
          {headerAction}
        </div>
        <VendorRoleDefinition role="source" className="mt-[4px]" />
      </div>

      <VendorSourcingCard
        org={org}
        dataMap={dataMap}
        source={source}
        collectionGroupId={isHr ? sourcingGroups.map((group) => group.id) : sourcingGroups[0].id}
        receivedData={receivedData}
        variant="modal"
      />
    </div>
  );
}

function VendorDocumentationContent({
  vendor,
  org,
  updateVendor,
  isDisabledWhileLoading,
  setIsDisabledWhileLoading,
  sellingAndSharingAnswers,
  allVendors,
  collectionContext,
}: {
  vendor: OrganizationDataRecipientDto;
  org: OrganizationDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  isDisabledWhileLoading: boolean;
  setIsDisabledWhileLoading: (value: boolean) => void;
  sellingAndSharingAnswers: SurveyAnswers;
  allVendors: OrganizationDataRecipientDto[];
  collectionContext: CollectionContext;
}) {
  const { disclosureMeta } = getVendorDocumentationDisclosureState(
    vendor,
    org,
    sellingAndSharingAnswers,
    allVendors,
  );
  const isEeaUkRegion = org.featureGdpr && vendor.processingRegions.includes("EEA_UK");
  const isThirdParty = isThirdPartyRecipient(vendor);
  const classificationQuestionProps = {
    org,
    vendor,
    updateVendor,
    setIsDisabledWhileLoading,
    sellingAndSharingAnswers,
    allVendors,
  };

  return (
    <Form
      onSubmit={() => {}}
      render={() => (
        <div className="space-y-[24px]">
          <ExplanatoryCard
            variant="hairline"
            expandable={false}
            topBody={
              <div>
                <h3 className="text-[16px] leading-[24px] font-semibold text-gray-800 mb-[16px]">
                  What is Data Documentation
                </h3>
                <p className="text-[14px] leading-[20px] text-neutral-500">
                  Different privacy regulations require documentation about vendors being used by a
                  business. This requirement can be fulfilled by uploading or linking to the
                  relevant documentation found within a vendor&apos;s Privacy Policy, Terms of
                  Service, Data Processing Agreements and/or other contracts/documents. These
                  documents can sometimes be challenging to find.
                </p>
              </div>
            }
          />

          <VendorDocumentationModalQuestion label="Vendor Contact Email for Classification Queries">
            <VendorDocumentationContactEmailField
              vendor={vendor}
              updateVendor={updateVendor}
              disabled={isDisabledWhileLoading}
              fieldName={`vendor-details-modal-${vendor.vendorId}-classification-contact-email`}
              showLabel={false}
            />
            {!isVendorDocumentationClassified(org, vendor, collectionContext) && (
              <div className="mt-[12px]">
                <VendorDocumentationEmailVendorSection
                  org={org}
                  vendor={vendor}
                  collectionContext={collectionContext}
                  updateVendor={updateVendor}
                  // Aquilo renders this parent modal at z-index 15001.
                  emailDialogZIndex={15002}
                />
              </div>
            )}
          </VendorDocumentationModalQuestion>

          <VendorDocumentationModalQuestion
            label="Regions"
            helpText={`From which region(s) is customer data sent to ${vendor.name}?`}
          >
            <VendorDocumentationRegionsField
              org={org}
              vendor={vendor}
              updateVendor={updateVendor}
              namePrefix={`vendor-details-modal-${vendor.vendorId}-`}
              regionCheckboxLayout="horizontal"
            />
          </VendorDocumentationModalQuestion>

          {isCaContractFieldRequiredForVendor(vendor) && (
            <VendorDocumentationModalQuestion
              label="California Contract Requirement"
              incomplete={!isVendorCaContractDocumentationComplete(vendor)}
              helpText={
                <>
                  Upload or link to a document below, otherwise contact this vendor for
                  documentation. Click{" "}
                  <a
                    href="https://knowledge.truevault.com/article/353-sample-california-contract-template"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    here
                  </a>{" "}
                  to access a contract template you can use to satisfy this requirement.
                </>
              }
            >
              <VendorDocumentationCaContractQuestion
                org={org}
                vendor={vendor}
                updateVendor={updateVendor}
                setIsDisabledWhileLoading={setIsDisabledWhileLoading}
              />
            </VendorDocumentationModalQuestion>
          )}

          {doesGppSignalApplyForVendor(vendor) && (
            <VendorDocumentationModalQuestion
              label="Browser Opt-Out"
              incomplete={!isVendorGppSignalDocumentationComplete(vendor)}
              helpText={gppSignalHelpText(vendor)}
            >
              <VendorDocumentationBrowserOptOutQuestion
                org={org}
                vendor={vendor}
                updateVendor={updateVendor}
                setIsDisabledWhileLoading={setIsDisabledWhileLoading}
              />
            </VendorDocumentationModalQuestion>
          )}

          {vendor.isPlatform !== true && (
            <VendorDocumentationModalQuestion
              label="Platform Requests"
              badge={vendor.vendorRespectsPlatformRequests ? <TruevaultVerifiedBadge /> : undefined}
              helpText={
                vendor.vendorRespectsPlatformRequests
                  ? "This vendor has been verified by TrueVault to respect platform-level opt-out signals."
                  : "Indicate whether this vendor respects platform-level opt-out signals and deletion requests from platforms like Shopify."
              }
            >
              <VendorDocumentationPlatformRequestsQuestion
                vendor={vendor}
                updateVendor={updateVendor}
                setIsDisabledWhileLoading={setIsDisabledWhileLoading}
              />
            </VendorDocumentationModalQuestion>
          )}

          {isUsClassificationRequiredForVendor(vendor, org, collectionContext) && (
            <VendorDocumentationModalQuestion
              label={isThirdParty ? "US Classification: Third Party" : "US Classification"}
              incomplete={!doesVendorHaveUsClassification(vendor)}
              helpText={usClassificationHelpText(vendor, disclosureMeta)}
            >
              <VendorDocumentationUsClassificationQuestion {...classificationQuestionProps} />
            </VendorDocumentationModalQuestion>
          )}

          {isEeaUkRegion && disclosureMeta.showGdprProcessorClassification && (
            <VendorDocumentationModalQuestion
              label="GDPR Classification"
              incomplete={!isVendorGdprClassificationComplete(vendor)}
              helpText={gdprClassificationHelpText(vendor)}
            >
              <VendorDocumentationGdprClassificationQuestion {...classificationQuestionProps} />
            </VendorDocumentationModalQuestion>
          )}

          {isEeaUkRegion && doesGdprInternationalDataTransferApplyForVendor(vendor) && (
            <VendorDocumentationModalQuestion
              label="International Data Transfers"
              incomplete={!isGdprInternationalDataTransferComplete(vendor)}
              helpText={
                <>
                  Data transfers to {vendor.name} are considered &quot;safe&quot; under GDPR only if{" "}
                  {vendor.name} participates in the EU-US Data Privacy Framework (DPF), if it
                  incorporates Standard Contractual Clauses (SCCs) into its agreements with your
                  business, or if consumer&apos;s personal data is transferred solely to fulfill a
                  contract with the consumer. If you are relying on {vendor.name}&apos;s DPF
                  participation or its SCCS for the transfer, add a link to its DPF profile page or
                  its online SCCS, or alternatively you can upload an agreement which contains the
                  SCCs.{" "}
                  <a
                    href="https://knowledge.truevault.com/article/202-standard-contractual-clauses"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 underline"
                  >
                    Read more about SCCs and how to look for them
                  </a>
                  .
                </>
              }
            >
              <VendorDocumentationInternationalDataTransfersQuestion
                {...classificationQuestionProps}
              />
            </VendorDocumentationModalQuestion>
          )}
        </div>
      )}
    />
  );
}

function VendorDocumentationModalQuestion({
  label,
  incomplete = false,
  badge,
  helpText,
  children,
}: {
  label: React.ReactNode;
  incomplete?: boolean;
  badge?: React.ReactNode;
  helpText?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-[12px]">
        <div className="text-[14px] font-medium text-neutral-900">{label}</div>
        {incomplete ? (
          <BadgedTextPill cellPillDisplay={{ color: "yellow", text: "Incomplete" }} />
        ) : (
          badge
        )}
      </div>
      {helpText && (
        <div className="mt-[6px] mb-[12px] text-[14px] leading-[20px] text-neutral-500">
          {helpText}
        </div>
      )}
      {children}
    </div>
  );
}
function ApiIntegrationContent() {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">API Integration</h3>
      <div className="flex gap-3 p-4 bg-primary-50 rounded-md border border-primary-100">
        <LightBulbIcon className="h-5 w-5 text-primary-600 shrink-0" />
        <div className="text-sm text-primary-900">
          <p className="font-semibold mb-1">This vendor can be connected to TrueVault.</p>
          <p>
            Go to{" "}
            <Link
              to={Routes.privacyCenter.integrations}
              className="underline hover:text-primary-700"
            >
              API Integrations page
            </Link>{" "}
            to connect this vendor to the platform to optimize automated request processing.
          </p>
        </div>
      </div>
    </div>
  );
}

interface DataStorageDeletionContentProps {
  radioGroupNamePrefix: string;
  vendor: OrganizationDataRecipientDto;
  org: OrganizationDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  isDisabledWhileLoading: boolean;
  setIsDisabledWhileLoading: (value: boolean) => void;
}

function DataStorageDeletionContent({
  radioGroupNamePrefix,
  vendor,
  org,
  updateVendor,
  isDisabledWhileLoading,
  setIsDisabledWhileLoading,
}: DataStorageDeletionContentProps) {
  const enqueueSnackbar = useSnackBar();

  const [fetchedDeletionInstructions, , refreshInstructions] = useRequestInstructions(
    org.id,
    "DELETE",
    undefined,
    true,
  );
  const { fetch: saveInstruction } = useActionRequest({
    api: async (newInstruction: RequestHandlingInstructionDto) => {
      await Api.requestHandlingInstructions.putInstruction(org.id, newInstruction);
    },
    onSuccess: refreshInstructions,
    onError: () => {
      enqueueSnackbar("Error updating vendor deletion settings", { variant: "error" });
    },
  });

  const { settings: apiIntegrations } = useIntegrationsSettings();

  const deletionInstruction = fetchedDeletionInstructions?.find(
    (ins) => ins.vendorId === vendor.vendorId,
  );

  if (!fetchedDeletionInstructions) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <DataStorageDeletionForm
      radioGroupNamePrefix={radioGroupNamePrefix}
      vendor={vendor}
      org={org}
      updateVendor={updateVendor}
      deletionInstruction={deletionInstruction}
      updateInstruction={saveInstruction}
      apiIntegrations={apiIntegrations}
      isDisabledWhileLoading={isDisabledWhileLoading}
      setIsDisabledWhileLoading={setIsDisabledWhileLoading}
    />
  );
}

function DataStorageDeletionForm({
  radioGroupNamePrefix,
  vendor,
  updateVendor,
  deletionInstruction,
  updateInstruction,
  apiIntegrations,
  isDisabledWhileLoading,
  setIsDisabledWhileLoading,
}: {
  radioGroupNamePrefix: string;
  vendor: OrganizationDataRecipientDto;
  org: OrganizationDto;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  deletionInstruction: RequestHandlingInstructionDto | undefined;
  updateInstruction: (v: RequestHandlingInstructionDto) => Promise<void>;
  apiIntegrations: PrivacyCenterIntegrationsDto;
  isDisabledWhileLoading: boolean;
  setIsDisabledWhileLoading: (value: boolean) => void;
}) {
  const [dataAccessibility, setDataAccessibility] = useState<DataAccessibility | null>(
    vendor.dataAccessibility,
  );
  const [dataDeletability, setDataDeletability] = useState<DataDeletability | null>(
    vendor.dataDeletability,
  );
  const [deleteProcessingMethod, setDeleteProcessingMethod] = useState<ProcessingMethod>(
    deletionInstruction?.processingMethod,
  );
  const [hasRetentionExceptions, setHasRetentionExceptions] = useState<boolean>(
    deletionInstruction?.hasRetentionExceptions ?? false,
  );

  let lockedValues = resolveDataAccessibilityForVendor(
    {
      ...vendor,
      dataAccessibility: dataAccessibility,
      dataDeletability: dataDeletability,
    },
    apiIntegrations,
  );

  let finalDataAccessibility = lockedValues.effectiveDataAccessibility;
  let allowEditingDataAccessibility = lockedValues.dataAccessibilityIsLocked !== true;

  let finalDeleteProcessingMethod = lockedValues.deletionProcessingMethod
    ? lockedValues.deletionProcessingMethod
    : deleteProcessingMethod;
  let allowEditingDeleteProcessingMethod = lockedValues.deletionProcessingMethodIsLocked !== true;
  let showDeleteProcessingMethod =
    (finalDataAccessibility == "ACCESSIBLE" || finalDataAccessibility == null) &&
    deletionInstruction;

  let finalDataDeletability = lockedValues.dataDeletability
    ? lockedValues.dataDeletability
    : dataDeletability;

  const getDeletionSelectedValue = (): string => {
    if (finalDataAccessibility == null) return "";
    if (finalDataDeletability === "NOT_DELETABLE") return "NOT_DELETABLE";
    if (finalDeleteProcessingMethod === "DELETE") return "DELETE";
    if (finalDeleteProcessingMethod === "RETAIN" && hasRetentionExceptions)
      return "PARTIAL_DELETED";
    if (finalDeleteProcessingMethod === "RETAIN") return "RETAIN";
    return "";
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm text-gray-600 mb-2">
          Your business will be required to delete personal information when customers make a
          Request to Delete.
        </p>
        <p className="text-sm text-gray-600">
          Note: TrueVault defaults to &quot;Yes&quot; for data accessibility and data deletion for
          most vendors.
        </p>
      </div>

      {/* Data Accessibility */}
      <div className="flex justify-between items-start gap-6">
        <div className="max-w-[400px]">
          <label className="block text-sm font-semibold text-gray-900 mb-1">
            Is data accessible?
          </label>
          <p className="text-sm text-gray-500">
            Answer &quot;yes&quot; if you can look up a customer in that system using a customer's
            name or email address. If you cannot-- answer &quot;no.&quot;
          </p>
        </div>
        <div className="flex-shrink-0 pt-1">
          <ToolTip text={lockedValues.dataAccessibilityLockedTooltipMessage} width="250px">
            <div
              className={clsx(!allowEditingDataAccessibility && "opacity-60 cursor-not-allowed")}
            >
              <RadioGroup
                name={`${radioGroupNamePrefix}-dataAccessibility`}
                direction="horizontal"
                options={[
                  { label: "Yes", value: "ACCESSIBLE" },
                  { label: "No", value: "NOT_ACCESSIBLE" },
                ]}
                selectedValue={
                  finalDataAccessibility === "NOT_ACCESSIBLE" ? "NOT_ACCESSIBLE" : "ACCESSIBLE"
                }
                onChange={async (newValue: string) => {
                  if (!allowEditingDataAccessibility || isDisabledWhileLoading) return;
                  const newDataAccessibility = newValue as DataAccessibility;
                  setDataAccessibility(newDataAccessibility);
                  setIsDisabledWhileLoading(true);

                  await updateVendor({
                    vendorId: vendor.vendorId,
                    vendorSettings: {
                      ...vendor,
                      dataAccessibility: newDataAccessibility,
                      classificationId: vendor.classification.id,
                    },
                  });

                  setIsDisabledWhileLoading(false);
                }}
              />
            </div>
          </ToolTip>
        </div>
      </div>

      {/* Data Deletion */}
      <div className="flex justify-between items-start gap-6">
        <div className="max-w-[400px]">
          <label className="block text-sm font-semibold text-gray-900 mb-1">
            Will data be deleted?
          </label>
          <p className="text-sm text-gray-500">
            if you can delete that data or if you have a business or legal basis for retaining the
            data.If you retain data, we&apos;ll ask you to select one or more reasons for retention
          </p>
        </div>
        <div className="flex-shrink-0 w-[220px] pt-1">
          {showDeleteProcessingMethod ? (
            <select
              value={getDeletionSelectedValue()}
              disabled={!allowEditingDeleteProcessingMethod}
              onChange={async (e) => {
                const selectedValue = e.target.value as
                  | ProcessingMethod
                  | "PARTIAL_DELETED"
                  | "NOT_DELETABLE"
                  | "";
                if (!selectedValue) return;

                let newHasRetentionExceptions = selectedValue === "PARTIAL_DELETED";
                let newDataDeletability: DataDeletability =
                  selectedValue === "NOT_DELETABLE" ? "NOT_DELETABLE" : "DELETABLE";
                let newDeleteProcessingMethod: ProcessingMethod =
                  selectedValue === "DELETE" ? "DELETE" : "RETAIN";
                let newDataAccessibility = finalDataAccessibility;
                let newSelectedRetentionReasons = deletionInstruction?.retentionReasons ?? [];

                if (finalDataAccessibility == null) {
                  newDataAccessibility = "ACCESSIBLE";
                  setDataAccessibility(newDataAccessibility);
                }

                if (newDataDeletability === "NOT_DELETABLE") {
                  newDeleteProcessingMethod = "RETAIN";
                }

                if (
                  finalDataDeletability === "DELETABLE" &&
                  newDataDeletability !== "DELETABLE" &&
                  (deletionInstruction?.retentionReasons ?? []).length === 0 &&
                  vendor.defaultRetentionReasons?.length > 0
                ) {
                  newSelectedRetentionReasons = vendor.defaultRetentionReasons;
                }

                setHasRetentionExceptions(newHasRetentionExceptions);
                setDataDeletability(newDataDeletability);
                setDeleteProcessingMethod(newDeleteProcessingMethod);
                setIsDisabledWhileLoading(true);

                await Promise.all([
                  updateVendor({
                    vendorId: vendor.vendorId,
                    vendorSettings: {
                      ...vendor,
                      dataAccessibility: newDataAccessibility,
                      dataDeletability: newDataDeletability,
                      classificationId: vendor.classification.id,
                    },
                  }),
                  updateInstruction({
                    ...deletionInstruction,
                    processingMethod: newDeleteProcessingMethod,
                    hasRetentionExceptions: newHasRetentionExceptions,
                    retentionReasons: newSelectedRetentionReasons,
                  }),
                ]);

                setIsDisabledWhileLoading(false);
              }}
              className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Make a selection</option>
              <option value="DELETE">Yes - data can be deleted</option>
              <option value="RETAIN">No - data must be retained</option>
              <option value="PARTIAL_DELETED">Partial - some data deleted</option>
              <option value="NOT_DELETABLE">No - not able to delete data</option>
            </select>
          ) : (
            <span className="text-sm text-gray-400 italic">
              {finalDataAccessibility === "NOT_ACCESSIBLE"
                ? "Not applicable"
                : "Select data accessibility first"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface PlaceholderContentProps {
  title: string;
}

function PlaceholderContent({ title }: PlaceholderContentProps) {
  return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      <p>{title} content placeholder</p>
    </div>
  );
}
